import React, { createContext, useContext, useState, useEffect } from 'react';
import { adminAuthAPI, AdminUserResponse, LoginRequest } from '../lib/api';
import { identifyAnalyticsUser, resetAnalyticsUser } from '../lib/analytics';
import { cabinetPath, currentCabinetRole } from '../lib/domains';

interface AdminAuthContextType {
    user: AdminUserResponse | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (data: LoginRequest) => Promise<void>;
    logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

export const AdminAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<AdminUserResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        if (currentCabinetRole() === 'merchant') {
            setIsLoading(false);
            return;
        }
        const checkAuth = async () => {
            const token = localStorage.getItem('admin_access_token');

            if (token) {
                try {
                    const userData = await adminAuthAPI.getCurrentUser();
                    setUser(userData);
                    setIsAuthenticated(true);
                    identifyAnalyticsUser('admin', userData);
                } catch (error) {
                    console.error('Failed to fetch admin user:', error);
                    logout();
                }
            }
            setIsLoading(false);
        };

        checkAuth();
    }, []);

    const login = async (data: LoginRequest) => {
        setIsLoading(true);
        try {
            const response = await adminAuthAPI.login(data);
            localStorage.setItem('admin_access_token', response.access_token);

            const userData = await adminAuthAPI.getCurrentUser();
            setUser(userData);
            setIsAuthenticated(true);
            identifyAnalyticsUser('admin', userData);
        } catch (error) {
            console.error('Login failed:', error);
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        adminAuthAPI.logout();
        setUser(null);
        setIsAuthenticated(false);
        resetAnalyticsUser();
        window.location.href = cabinetPath('admin', '/login');
    };

    return (
        <AdminAuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout }}>
            {children}
        </AdminAuthContext.Provider>
    );
};

export const useAdminAuth = () => {
    const context = useContext(AdminAuthContext);
    if (context === undefined) {
        throw new Error('useAdminAuth must be used within an AdminAuthProvider');
    }
    return context;
};
